<template>
  <b-container>
    <b-row align-h="start" class="mb-4">
      <b-col>
        <h1>My Account</h1>
      </b-col>
      <b-col class="text-right">
        <saved-notice v-if="accountUpdated" class="m-1" />
        <b-button variant="primary" class="m-1" @click="onUpdateAccount">
          <template v-if="accountLoading">
            <button-loader />
          </template>
          <template v-else>Save</template>
        </b-button>
      </b-col>
    </b-row>
    <b-row>
      <b-col lg="6" sm="12">
        <error-alert :errors="accountErrors" class="mb-4" />
        <b-form class="base-form-col" @submit.prevent="onUpdateAccount">
          <b-form-group label="First Name">
            <b-form-input v-model="userFirstName" placeholder="First Name" />
          </b-form-group>
          <b-form-group label="Last Name">
            <b-form-input v-model="userLastName" placeholder="Last Name" />
          </b-form-group>
          <b-form-group label="Phone">
            <vue-tel-input v-model="userPhone" @input="onPhoneChange" />
          </b-form-group>
          <b-form-group label="Email">
            <b-form-input
              v-model="userEmail"
              type="email"
              placeholder="Email"
            />
          </b-form-group>
          <b-form-checkbox
            v-model="userSmsEnabled"
            :value="true"
            :unchecked-value="false"
            class="mt-3"
            >SMS Enabled?</b-form-checkbox
          >
        </b-form>
      </b-col>
      <b-col lg="6" sm="12" class="mt-2 mt-lg-0">
        <p v-if="passwordUpdated" class="success-text">
          Your password has been updated!
        </p>
        <error-alert :errors="passwordErrors" class="mb-4" />
        <div class="base-form-col">
          <b-form
            @submit.prevent="onUpdatePassword"
            @keyup.enter="onUpdatePassword"
          >
            <b-form-group label="Update Password">
              <div class="password-input" data-recording-ignore="mask">
                <b-form-input
                  v-model="currentPassword"
                  required
                  :type="showCurrentPassword ? 'text' : 'password'"
                  placeholder="Enter current password..."
                />
                <button
                  type="button"
                  class="btn btn-link link-black"
                  @click.prevent="showCurrentPassword = !showCurrentPassword"
                >
                  <span class="icon">
                    <img
                      v-if="showCurrentPassword"
                      :src="asset('/imgs/icons/show.png')"
                      alt="show"
                      title="Show"
                    />
                    <img
                      v-else
                      :src="asset('/imgs/icons/hide.png')"
                      alt="Hide"
                      title="Hide"
                    />
                  </span>
                </button>
              </div>
            </b-form-group>
            <b-form-group>
              <div class="password-input" data-recording-ignore="mask">
                <b-form-input
                  v-model="newPassword"
                  required
                  :type="showNewPassword ? 'text' : 'password'"
                  placeholder="Enter new password..."
                />
                <button
                  type="button"
                  class="btn btn-link link-black"
                  @click.prevent="showNewPassword = !showNewPassword"
                >
                  <span class="icon">
                    <img
                      v-if="showNewPassword"
                      :src="asset('/imgs/icons/show.png')"
                      alt="show"
                      title="Show"
                    />
                    <img
                      v-else
                      :src="asset('/imgs/icons/hide.png')"
                      alt="Hide"
                      title="Hide"
                    />
                  </span>
                </button>
              </div>
            </b-form-group>
            <b-form-group>
              <div class="password-input" data-recording-ignore="mask">
                <b-form-input
                  v-model="confirmPassword"
                  required
                  :type="showConfirmPassword ? 'text' : 'password'"
                  placeholder="Confirm new password..."
                />
                <button
                  type="button"
                  class="btn btn-link link-black"
                  @click.prevent="showConfirmPassword = !showConfirmPassword"
                >
                  <span class="icon">
                    <img
                      v-if="showConfirmPassword"
                      :src="asset('/imgs/icons/show.png')"
                      alt="show"
                      title="Show"
                    />
                    <img
                      v-else
                      :src="asset('/imgs/icons/hide.png')"
                      alt="Hide"
                      title="Hide"
                    />
                  </span>
                </button>
              </div>
            </b-form-group>
            <b-button variant="primary" type="submit">
              <template v-if="passwordLoading">
                <button-loader />
              </template>
              <template v-else>Update</template>
            </b-button>
          </b-form>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { mapGetters } from 'vuex';
import { VueTelInput } from 'vue-tel-input';
import SessionAPI from '../../services/SessionAPI';
import ButtonLoader from '../../components/ButtonLoader';
import SavedNotice from '../../components/SavedNotice';

export default {
  name: 'Account',
  components: {
    ButtonLoader,
    SavedNotice,
    VueTelInput,
  },
  data() {
    return {
      accountLoading: false,
      accountErrors: {},
      accountUpdated: false,
      passwordLoading: false,
      passwordErrors: {},
      passwordUpdated: false,
      userFirstName: null,
      userLastName: null,
      userPhone: null,
      userPhoneInternational: null,
      userEmail: null,
      userSmsEnabled: false,
      updatePassword: null,
      phoneInternational: false,
      currentPassword: null,
      showCurrentPassword: false,
      newPassword: null,
      showNewPassword: false,
      confirmPassword: null,
      showConfirmPassword: false,
    };
  },
  computed: {
    ...mapGetters('session', [
      'firstName',
      'lastName',
      'email',
      'phone',
      'smsEnabled',
    ]),
  },
  watch: {
    firstName(val) {
      this.userFirstName = val;
    },
    lastName(val) {
      this.userLastName = val;
    },
    email(val) {
      this.userEmail = val;
    },
    phone(val) {
      if (!this.userPhone) {
        this.userPhone = String(val);
      }
      this.userPhoneInternational = String(val);
    },
    smsEnabled(val) {
      this.userSmsEnabled = val;
    },
  },
  mounted() {
    if (!this.userFirstName) {
      this.userFirstName = this.firstName;
    }

    if (!this.userLastName) {
      this.userLastName = this.lastName;
    }

    if (!this.userEmail) {
      this.userEmail = this.email;
    }

    if (!this.userPhone) {
      this.userPhone = this.phone;
      this.userPhoneInternational = this.phone;
    }
  },
  methods: {
    onPhoneChange(number, data) {
      if (data.isValid) {
        this.userPhoneInternational = data.number.e164.replace('+', '');
      } else {
        this.userPhoneInternational = false;
      }
    },
    onUpdateAccount() {
      if (this.accountLoading) {
        return;
      }

      this.passwordUpdated = false;
      this.accountLoading = true;
      this.accountUpdated = false;
      this.accountErrors = {};

      SessionAPI.updateAccount({
        first_name: this.userFirstName,
        last_name: this.userLastName,
        email: this.userEmail,
        phone: this.userPhoneInternational,
        sms_enabled: this.userSmsEnabled,
      })
        .then((response) => {
          this.accountLoading = false;
          this.accountUpdated = true;
          setTimeout(() => {
            this.accountUpdated = false;
          }, 5000);
          this.$store.dispatch('session/load', response.data);
        })
        .catch((error) => {
          this.accountLoading = false;
          this.accountErrors = error.response.data.errors;
        });
    },
    onUpdatePassword() {
      if (this.passwordLoading) {
        return;
      }

      this.accountUpdated = false;
      this.passwordLoading = true;
      this.passwordUpdated = false;
      this.passwordErrors = {};

      SessionAPI.changePassword({
        current_password: this.currentPassword,
        password: this.newPassword,
        password_confirmed: this.confirmPassword,
      })
        .then((response) => {
          this.passwordLoading = false;
          this.passwordUpdated = true;
          this.currentPassword = null;
          this.newPassword = null;
          this.confirmPassword = null;
        })
        .catch((error) => {
          this.passwordLoading = false;
          this.passwordErrors = error.response.data.errors;
        });
    },
  },
};
</script>
